"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _utils = require("../utils");
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _StackView = _interopRequireWildcard(require("../StackView"));
var _Card = _interopRequireDefault(require("../Card"));
var _Fieldset = _interopRequireDefault(require("../Fieldset"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const [selectItemProps, selectedItemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.viewProps]);
const uniqueFields = ['id'];
const CardGroup = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    copy = 'en',
    tokens = {},
    variant = {},
    items = [],
    validation,
    feedback,
    initialCheckedIds,
    checkedIds,
    onChange,
    role = 'radiogroup',
    isInactive,
    name: inputGroupName,
    dictionary = _dictionary.default,
    ...rest
  } = _ref;
  const getCopy = (0, _utils.useCopy)({
    dictionary,
    copy
  });
  const viewport = (0, _ViewportProvider.useViewport)();
  const cardGroupTokens = (0, _ThemeProvider.useThemeTokens)('CardGroup', tokens, variant, {
    viewport
  });
  const {
    space,
    fieldSpace,
    direction,
    showIcon,
    outlineWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    outlineOffset,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight
  } = cardGroupTokens;
  const borderTokens = {
    outlineWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    outlineOffset
  };
  const singleSelection = role === 'radiogroup';
  const inputControl = {
    value: checkedIds,
    initialValue: initialCheckedIds,
    onChange,
    readOnly: isInactive
  };
  const singleInputValue = (0, _utils.useInputValue)(inputControl);
  const multipleInputValues = (0, _utils.useMultipleInputValues)(inputControl);
  const StackContainer = direction === 'row' ? _StackView.StackWrap : _StackView.default;
  if (!(0, _utils.containUniqueFields)(items, uniqueFields)) {
    throw new Error(`CardGroup items must have unique ${uniqueFields.join(', ')}`);
  }
  const handleChange = _react.default.useCallback((checked, event, cardId, itemOnChange) => {
    if (checked && singleSelection) {
      singleInputValue.setValue(cardId, event);
    } else {
      multipleInputValues.toggleOneValue(cardId, event);
    }
    return itemOnChange?.(checked, event);
  }, [singleInputValue, multipleInputValues, singleSelection]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Fieldset.default, {
    copy: copy,
    ref: ref,
    name: inputGroupName,
    space: fieldSpace,
    feedback: feedback,
    validation: validation,
    tokens: borderTokens,
    inactive: isInactive,
    showErrorBorder: true,
    showIcon: showIcon,
    accessibilityRole: role,
    ...selectProps(rest),
    children: props => /*#__PURE__*/(0, _jsxRuntime.jsx)(StackContainer, {
      space: space,
      children: items.map((_ref2, index) => {
        let {
          id,
          interactiveCard,
          card,
          onPress: itemOnChange,
          ...itemRest
        } = _ref2;
        const cardId = id || `${getCopy('prefixId')}[${index}]`;
        const itemHandleChange = (checked, event) => handleChange(checked, event, cardId, itemOnChange);
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Card.default, {
          id: cardId,
          onPress: itemHandleChange,
          interactiveCard: {
            ...interactiveCard,
            tokens: {
              paddingTop,
              paddingBottom,
              paddingLeft,
              paddingRight,
              ...interactiveCard?.tokens
            },
            variant: {
              isControl: true,
              selected: singleSelection ? singleInputValue.currentValue === cardId : multipleInputValues.currentValues.includes(cardId),
              inactive: isInactive
            },
            selectionType: role
          },
          ...selectItemProps(itemRest),
          ...props,
          children: card?.body
        }, cardId);
      })
    })
  });
});
CardGroup.displayName = 'CardGroup';
const dictionaryContentShape = _propTypes.default.shape({
  prefixId: _propTypes.default.string.isRequired
});
CardGroup.propTypes = {
  ...selectedSystemPropTypes,
  copy: _utils.copyPropTypes,
  /**
   * Override the default dictionary, by passing the complete dictionary object for `en` and `fr`
   */
  dictionary: _propTypes.default.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  }),
  /**
   * Optional theme token overrides for the outer RadioCardGroup component
   */
  tokens: (0, _utils.getTokensPropType)('CardGroup'),
  /**
   * Theme variants, shared between both RadioCardGroup and RadioCard
   */
  variant: _utils.variantProp.propType,
  /**
   * Array of objects containing specifics for each Card to be rendered in the group.
   */
  items: _propTypes.default.arrayOf(_propTypes.default.exact({
    ...selectedItemPropTypes,
    id: _propTypes.default.string,
    interactiveCard: _propTypes.default.object,
    card: _propTypes.default.object,
    onPress: _propTypes.default.func
  })),
  /**
   * Current validation status of the group, passed to the feedback element if there is one.
   */
  validation: _propTypes.default.oneOf(['error', 'success']),
  /**
   * If provided, a Feedback element is rendered containing this text.
   */
  feedback: _propTypes.default.string,
  /**
   * If provided, the card with this id is selected on first render.
   * If the role is radiogroup, it's a string
   * If the role is checkbox, it's a array of string.
   * Can have 'null' as value
   */
  initialCheckedIds: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.arrayOf(_propTypes.default.string)]),
  /**
   * If not undefined, the card with this id is selected (or none is selected if `null`), and the
   * element's selection state will be controlled by its parent using the `onChange` function.
   * If the role is radiogroup, it's a string
   * If the role is checkbox, it's a array of string.
   * Can have 'null' as value
   */
  checkedIds: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.arrayOf(_propTypes.default.string)]),
  /**
   * Function to call on change in selection state. Is required if the selection state is controlled
   * by a parent using checkedId and the input is not readOnly.
   */
  onChange: _propTypes.default.func,
  /**
   * Renders the card group like a radio card group or a checkbox card group
   */
  role: _propTypes.default.oneOf(['radiogroup', 'checkbox']),
  /**
   * If true, the card cannot be interacted with, elements are set as `disabled` and if the
   * theme supports `inactive` appearances rules, these are applied.
   */
  isInactive: _propTypes.default.bool,
  /**
   * On Web, this is passed to the `name` attribute of the fieldset and each input.
   */
  name: _propTypes.default.string
};
var _default = exports.default = CardGroup;